import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import reverse from 'lodash/reverse'
import sortBy from 'lodash/sortBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import type LeaveModel from 'modules/timeoff/store/models/Leave'

import Section, { LeaveSectionType } from './Section'

interface Props {
  employeeId: string
  employeePreferredName: string | null | undefined
  employeeCanApproveLeaves: boolean
  employeeCanUpdateLeaves: boolean
  employeeCanManageLeaveDocuments: boolean
  employeeTimeoffPolicyId: string | null | undefined
  year: number
}

interface LeavesData {
  leave: LeaveModel
  data: {
    today: moment.Moment
    beggining: moment.Moment
    finish: moment.Moment | null
    isCurrent: boolean
    isApproved: boolean
    upcoming: boolean
    isMedical: boolean
  }
}

const groupBy = (leavesData: LeavesData[], type: LeaveSectionType): LeaveModel[] => {
  const groupedLeaves = leavesData
    .filter(({ leave, data }) => {
      const { today, finish, isCurrent, upcoming, isMedical, isApproved } = data

      switch (type) {
        case 'medical':
          return (isCurrent || upcoming) && isMedical

        case 'upcoming':
          return (isCurrent || upcoming) && isApproved && !isMedical

        case 'past':
          return today.isAfter(finish) && isApproved

        case 'rejected':
          return leave.attributes.approved === false

        case 'pending_review':
          return !isMedical && (isNull(isApproved) || isUndefined(isApproved))

        default:
          return true
        // return all
      }
    })
    .map(({ leave }) => leave)

  if (type === 'past') {
    return reverse(sortBy(groupedLeaves, (l: LeaveModel) => l.attributes.finish_on))
  }

  return sortBy(groupedLeaves, (l: LeaveModel) => l.attributes.start_on)
}

const LeavesList = ({
  employeeId,
  employeePreferredName,
  employeeCanApproveLeaves,
  employeeCanUpdateLeaves,
  employeeCanManageLeaveDocuments,
  employeeTimeoffPolicyId,
  year,
}: Props) => {
  const { currentAndFutureLeaves } = useLeaves()

  const leavesData: LeavesData[] = currentAndFutureLeaves
    .filter({
      employee_id: employeeId,
    })
    .map((leave) => {
      const today = moment()
      const beggining = moment(leave.attributes.start_on).startOf('day')
      const finish = leave.attributes.finish_on
        ? moment(leave.attributes.finish_on).endOf('day')
        : null
      const isCurrent =
        today.isSameOrAfter(beggining) && (finish === null || today.isSameOrBefore(finish))
      const isApproved = leave.attributes.approved
      const upcoming = today.isBefore(beggining)
      const isMedical = leave.leaveType?.isMedicalLeave ?? false

      return {
        leave,
        data: {
          today,
          beggining,
          finish,
          isCurrent,
          isApproved,
          upcoming,
          isMedical,
        },
      }
    })

  const medical = groupBy(leavesData, 'medical')
  const past = groupBy(leavesData, 'past')
  const pendingReview = groupBy(leavesData, 'pending_review')
  const rejected = groupBy(leavesData, 'rejected')
  const upcoming = groupBy(leavesData, 'upcoming')

  const employeeProps = {
    employeeId,
    employeePreferredName,
    employeeCanApproveLeaves,
    employeeCanUpdateLeaves,
    employeeCanManageLeaveDocuments,
    employeeTimeoffPolicyId,
  }

  return (
    <>
      {medical.length > 0 && (
        <Section leaves={medical} type="medical" year={year} {...employeeProps} />
      )}
      {pendingReview.length > 0 && (
        <Section leaves={pendingReview} type="pending_review" year={year} {...employeeProps} />
      )}
      {upcoming.length > 0 && (
        <Section leaves={upcoming} type="upcoming" year={year} {...employeeProps} />
      )}
      <Section leaves={past} type="past" year={year} {...employeeProps} />
      {rejected.length > 0 && (
        <Section leaves={rejected} type="rejected" year={year} {...employeeProps} />
      )}
    </>
  )
}

export default observer(LeavesList)
